import { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function Notifications() {
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [selectedIds, setSelectedIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectAnchorEl, setSelectAnchorEl] = useState(null);
  const [filter, setFilter] = useState({ status: null, starred: null });
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tempSearch, setTempSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(tempSearch);
      setPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(handler);
  }, [tempSearch]);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch current user details
  const fetchMe = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    retry: false,
  });

  useEffect(() => {
    if (meData?.user_id) {
      setUserId(meData.user_id);
    }
  }, [meData]);

  // Fetch notifications with pagination
  const fetchNotifications = async ({ queryKey }) => {
    const [_key, userId, page, filter, searchTerm] = queryKey;
    if (!userId) return { data: [], totalCount: 0 };
    const params = { page, limit };
    if (filter.status !== null) params.status = filter.status;
    if (filter.starred !== null) params.starred = filter.starred;
    if (searchTerm) params.search = searchTerm;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/notifications/${userId}`,
      {
        params,
        withCredentials: true
      }
    );
    return response.data;
  };

  const {
    data: notificationsData,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ["notifications", userId, page, filter, searchTerm],
    queryFn: fetchNotifications,
    enabled: !!userId,
  });

  const notifications = notificationsData?.data || [];
  const totalCount = notificationsData?.totalCount || 0;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(notifications.map((n) => n.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleSelectMenuOpen = (event) => setSelectAnchorEl(event.currentTarget);
  const handleSelectMenuClose = () => setSelectAnchorEl(null);

  const handleToggleStar = async (notifId, currentStarred) => {
    setLoading(true);
    const newStatus = currentStarred === 1 ? 0 : 1;
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/star/${notifId}/${newStatus}`,
        { user_id: userId },
        { withCredentials: true }
      );

      if (res.status === 200) {
        setSnackbar({
          open: true,
          message: res.data.message || "Updated successfully",
          severity: "success",
        });
        // Wait a bit before refetching to avoid unauthorized issue reported by user
        setTimeout(() => {
          refetch();
        }, 500);
      }
    } catch (err) {
      console.error("Error toggling star:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectByType = (type) => {
    handleSelectMenuClose();
    setPage(1); // Reset to first page on filter change
    switch (type) {
      case "all":
        setFilter({ status: null, starred: null });
        break;
      case "none":
        setFilter({ status: null, starred: null });
        setSelectedIds([]);
        break;
      case "read":
        setFilter({ status: 10, starred: null });
        break;
      case "unread":
        setFilter({ status: 9, starred: null });
        break;
      case "starred":
        setFilter({ status: null, starred: 1 });
        break;
      case "unstarred":
        setFilter({ status: null, starred: 0 });
        break;
      default:
        break;
    }
  };

  const handleBulkStar = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);

    const selectedNotifications = notifications.filter((n) => selectedIds.includes(n.id));
    const allStarred = selectedNotifications.every((n) => n.starred === 1);
    const targetStatus = allStarred ? 0 : 1;

    try {
      const idString = selectedIds.join(",");
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/star/${idString}/${targetStatus}`,
        { user_id: userId },
        { withCredentials: true }
      );

      setSnackbar({
        open: true,
        message: res.data.message || "Updated successfully",
        severity: "success",
      });

      setTimeout(() => {
        refetch();
        setSelectedIds([]);
      }, 500);
    } catch (err) {
      console.error("Error bulk toggling star:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAll = async (statusId) => {
    handleMenuClose();
    if (notifications.length === 0) return;
    setLoading(true);

    try {
      const idString = notifications.map(n => n.id).join(",");
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/read/${idString}/${statusId}`,
        { user_id: userId },
        { withCredentials: true }
      );

      setSnackbar({
        open: true,
        message: res.data.message || "Updated successfully",
        severity: "success",
      });

      setTimeout(() => {
        refetch();
      }, 500);
    } catch (err) {
      console.error("Error marking all as seen:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkMarkStatus = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);

    const selectedNotifications = notifications.filter((n) => selectedIds.includes(n.id));
    const allRead = selectedNotifications.every((n) => n.status === 10);
    const targetStatus = allRead ? 9 : 10;

    try {
      const idString = selectedIds.join(",");
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/notifications/read/${idString}/${targetStatus}`,
        { user_id: userId },
        { withCredentials: true }
      );

      setSnackbar({
        open: true,
        message: res.data.message || "Updated successfully",
        severity: "success",
      });

      setTimeout(() => {
        refetch();
        setSelectedIds([]);
      }, 500);
    } catch (err) {
      console.error("Error bulk marking status:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);

    try {
      const idString = selectedIds.join(",");
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/notifications/delete/${idString}`,
        {
          data: { user_id: userId },
          withCredentials: true
        }
      );

      setSnackbar({
        open: true,
        message: res.data.message || "Deleted successfully",
        severity: "success",
      });

      setTimeout(() => {
        refetch();
        setSelectedIds([]);
      }, 1000);
    } catch (err) {
      console.error("Error bulk deleting notifications:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "An error occurred",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNotifClick = async (notif) => {
    // If not expanded, expand first and mark as read
    if (expandedId !== notif.id) {
      setExpandedId(notif.id);

      // If it's unread, mark as read
      if (notif.status === 9) {
        try {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/notifications/read/${notif.id}/10`,
            { user_id: userId },
            { withCredentials: true }
          );
          refetch();
        } catch (err) {
          console.error("Error updating notification status:", err);
        }
      }
      return;
    }

    // If already expanded, proceed with navigation logic
    // If it's a lesson notification, navigate
    if (notif.menu_id === 11) {
      navigate(`/lessons/details/${notif.field_id}`);
    }

    // If it's an event notification, navigate
    if (notif.menu_id === 12) {
      navigate(`/events/add`, { state: { field_id: notif.field_id } });
    }

    // If it's a leave request notification, navigate
    if (notif.menu_id === 13) {
      if (notif.send_user_id === 1) {
        // From admin (Approved/Declined) - Navigate to list and filter
        navigate(`/leave-list`, { state: { filterId: notif.field_id } });
      } else {
        // To admin (New Request) - Navigate to specific item and open dialog
        navigate(notif.field_id ? `/leave-list/${notif.field_id}` : `/leave-list`);
      }
    }

    // If it's a ticket notification, navigate
    if (notif.menu_id === 14) {
      if (notif.field_id) {
        navigate(`/tickets-edit/${notif.field_id}`);
      }
    }

    // If it's a claims notification, navigate
    if (notif.menu_id === 15) {
      try {
        // Extract month from content like "2025-11"
        const monthMatch = notif.content.match(/(\d{4}-\d{2})/);
        const month = monthMatch ? monthMatch[1] : null;

        navigate(`/claims/list/${notif.send_user_id}`, {
          state: {
            fromMonth: month,
            toMonth: month,
            fromNotification: true
          }
        });
      } catch (error) {
        console.error("Error parsing claims notification:", error);
        // Fallback navigation
        navigate(`/claims/list/${notif.send_user_id}`);
      }
    }

    // If it's a weekly report notification, navigate
    if (notif.menu_id === 16) {
      // Extract MM YYYY from content "Week X (MM YYYY)" or "Week X (MM - YYYY)"
      const match = notif.content.match(/\(\s*(\d{1,2})\s*[- ]\s*(\d{4})\s*\)/);
      if (match) {
        const month = match[1];
        const year = match[2];
        navigate(`/weekly-report`, {
          state: {
            month: parseInt(month, 10),
            year: parseInt(year, 10),
            userId: notif.send_user_id
          }
        });
      } else {
        // Fallback if parsing fails
        navigate(`/weekly-report`, {
          state: {
            userId: notif.send_user_id
          }
        });
      }
    }

    // If it's a mission progress notification, navigate
    if (notif.menu_id === 17) {
      try {
        // Extract month (e.g., "January 2026") from content
        const monthMatch = notif.content.match(/for\s+([A-Za-z]+\s+\d{4})/);
        // Extract week (e.g., "week3") from content
        const weekMatch = notif.content.match(/Week\s+(week\d+)/i);

        const month = monthMatch ? monthMatch[1] : null; // "January 2026"
        const weekId = weekMatch ? weekMatch[1] : null; // "week3"
        const userId = notif.send_user_id; // 83

        navigate(`/mission/${userId}`, {
          state: {
            month: month,
            weekId: weekId,
            userId: userId,
            fromNotification: true
          }
        });
      } catch (error) {
        console.error("Error parsing mission notification:", error);
        // Fallback navigation
        navigate(`/mission/${notif.send_user_id}`);
      }
    }

    // If it's a broadcast email notification, navigate
    if (notif.menu_id === 18) {
      navigate(`/broadcast-email`, {
        state: {
          broadcastId: notif.field_id,
          fromNotification: true
        }
      });
    }

    // If it's a claims reminder notification, navigate
    if (notif.menu_id === 19) {
      try {
        // Extract month from content like "January 2026"
        const monthMatch = notif.content.match(/for\s+([A-Za-z]+\s+\d{4})/);
        const month = monthMatch ? monthMatch[1] : null;

        navigate(`/claims/add`, {
          state: {
            month: month,
            fromNotification: true
          }
        });
      } catch (error) {
        console.error("Error parsing claims reminder notification:", error);
        // Fallback navigation
        navigate(`/claims/add`);
      }
    }

    // If it's a salary update notification, navigate
    if (notif.menu_id === 20) {
      navigate(`/user-dashboard`, { state: { scrollToSalary: true } });
    }

    // If it's a publication notification, navigate
    if (notif.menu_id === 21) {
      navigate(`/publications/list`, { state: { field_id: notif.field_id } });
    }
  };

  const handleToggleExpand = (e, notifId) => {
    e.stopPropagation();
    setExpandedId(expandedId === notifId ? null : notifId);
  };

  const handleRefresh = () => {
    setSelectedIds([]);
    setPage(1);
    setFilter({ status: null, starred: null });
    setTempSearch("");
    // This will trigger a refetch automatically because queryKey changes
  };

  const handlePageNext = () => {
    if (page * limit < totalCount) setPage((prev) => prev + 1);
  };

  const handlePagePrev = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const startRange = (page - 1) * limit + 1;
  const endRange = Math.min(page * limit, totalCount);

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={3} width="100%">
        <Card sx={{
          width: "100%",
          height: "100%",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "hidden"
        }}>
          {/* Toolbar */}
          <MDBox
            p={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderBottom: "1px solid #f0f2f5" }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox display="flex" alignItems="center">
                <Checkbox
                  size="small"
                  indeterminate={selectedIds.length > 0 && selectedIds.length < notifications.length}
                  checked={notifications.length > 0 && selectedIds.length === notifications.length}
                  onChange={handleSelectAll}
                  sx={{ p: 0.5 }}
                />
                <IconButton size="small" onClick={handleSelectMenuOpen} sx={{ ml: -1 }}>
                  <Icon sx={{ fontSize: "1rem !important" }}>arrow_drop_down</Icon>
                </IconButton>
                <Menu
                  anchorEl={selectAnchorEl}
                  open={Boolean(selectAnchorEl)}
                  onClose={handleSelectMenuClose}
                  PaperProps={{
                    sx: { mt: 1, boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }
                  }}
                >
                  <MenuItem onClick={() => handleSelectByType("all")} sx={{ fontSize: "0.875rem" }}>All</MenuItem>
                  <MenuItem onClick={() => handleSelectByType("none")} sx={{ fontSize: "0.875rem" }}>None</MenuItem>
                  <MenuItem onClick={() => handleSelectByType("read")} sx={{ fontSize: "0.875rem" }}>Read</MenuItem>
                  <MenuItem onClick={() => handleSelectByType("unread")} sx={{ fontSize: "0.875rem" }}>Unread</MenuItem>
                  <MenuItem onClick={() => handleSelectByType("starred")} sx={{ fontSize: "0.875rem" }}>Starred</MenuItem>
                  <MenuItem onClick={() => handleSelectByType("unstarred")} sx={{ fontSize: "0.875rem" }}>Unstarred</MenuItem>
                </Menu>
              </MDBox>
              <IconButton size="small" onClick={handleRefresh} disabled={isFetching}>
                <Icon sx={{ fontSize: "1.2rem !important" }}>refresh</Icon>
              </IconButton>

              <IconButton size="small" onClick={handleMenuOpen}>
                <Icon sx={{ fontSize: "1.2rem !important" }}>more_vert</Icon>
              </IconButton>

              {/* Search Area */}
              <MDBox
                sx={{
                  ml: 1,
                  width: { xs: "120px", sm: "200px", md: "250px" },
                  position: "relative",
                  bgcolor: "#f1f3f4",
                  borderRadius: "24px",
                  px: 2,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: "1px solid transparent",
                  "&:focus-within": {
                    bgcolor: "white",
                    boxShadow: "0 1px 1px 0 rgba(65,69,73,0.3), 0 1px 3px 1px rgba(65,69,73,0.15)",
                    width: { xs: "150px", sm: "250px", md: "350px" },
                    borderColor: "#e0e0e0"
                  }
                }}
              >
                <Icon sx={{ color: "#5f6368", mr: 1, fontSize: "1.1rem !important" }}>search</Icon>
                <InputBase
                  placeholder="Search notifications..."
                  value={tempSearch}
                  onChange={(e) => setTempSearch(e.target.value)}
                  sx={{
                    fontSize: "0.875rem",
                    color: "#3c4043",
                    width: "100%",
                    "& input::placeholder": {
                      color: "#5f6368",
                      opacity: 0.8
                    }
                  }}
                />
                {tempSearch && (
                  <IconButton
                    size="small"
                    onClick={() => setTempSearch("")}
                    sx={{
                      p: 0,
                      color: "#5f6368",
                      "&:hover": { color: "#202124" }
                    }}
                  >
                    <Icon sx={{ fontSize: "1.1rem !important" }}>close</Icon>
                  </IconButton>
                )}
              </MDBox>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                {notifications.every((n) => n.status === 10) ? (
                  <MenuItem onClick={() => handleMarkAll(9)}>Mark all as unread</MenuItem>
                ) : (
                  <MenuItem onClick={() => handleMarkAll(10)}>Mark all as read</MenuItem>
                )}
              </Menu>

              {selectedIds.length > 0 && (
                <MDBox display="flex" alignItems="center" ml={2} gap={1}>
                  <Tooltip title={selectedIds.length === notifications.length ? "Delete all" : "Delete"}>
                    <IconButton size="small" onClick={handleBulkDelete}>
                      <Icon color="error">delete</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      notifications.filter(n => selectedIds.includes(n.id)).every(n => n.status === 10)
                        ? "Mark as unread"
                        : "Mark as read"
                    }
                  >
                    <IconButton size="small" onClick={handleBulkMarkStatus}>
                      <Icon>
                        {notifications.filter(n => selectedIds.includes(n.id)).every(n => n.status === 10)
                          ? "mark_email_unread"
                          : "mark_email_read"}
                      </Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      notifications.filter(n => selectedIds.includes(n.id)).every(n => n.starred === 1)
                        ? "Remove from Favorite"
                        : "Add to Favorite"
                    }
                  >
                    <IconButton size="small" onClick={handleBulkStar}>
                      <Icon color={notifications.filter(n => selectedIds.includes(n.id)).every(n => n.starred === 1) ? "warning" : "inherit"}>
                        {notifications.filter(n => selectedIds.includes(n.id)).every(n => n.starred === 1) ? "star" : "star_border"}
                      </Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              )}
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={2}>
              <MDTypography variant="button" color="text" fontWeight="regular">
                {totalCount > 0 ? `${startRange}-${endRange} of ${totalCount}` : "0-0 of 0"}
              </MDTypography>
              <MDBox display="flex">
                <IconButton size="small" onClick={handlePagePrev} disabled={page === 1}>
                  <Icon>chevron_left</Icon>
                </IconButton>
                <IconButton size="small" onClick={handlePageNext} disabled={endRange >= totalCount}>
                  <Icon>chevron_right</Icon>
                </IconButton>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* List */}
          <MDBox sx={{ minHeight: "60vh" }}>
            {isLoading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress color="info" />
              </MDBox>
            ) : notifications.length === 0 ? (
              <MDBox p={5} textAlign="center">
                <MDTypography variant="h6" color="text" fontWeight="light">
                  No notifications to show.
                </MDTypography>
              </MDBox>
            ) : (
              notifications.map((notif, index) => {
                const isUnread = notif.status === 9;
                const isSelected = selectedIds.includes(notif.id);
                const isEven = index % 2 === 0;

                // Alternating colors based on unread status and row index
                const bgColor = isUnread
                  ? (isEven ? "#f2f6fc" : "#e8f0fe")
                  : (isEven ? "#ffffff" : "#f9f9f9");

                const hoverBgColor = isUnread
                  ? (isEven ? "#ebf2ff" : "#e1eaff")
                  : (isEven ? "#f7f9fc" : "#f1f3f6");

                return (
                  <MDBox
                    key={notif.id}
                    px={1}
                    py={1.5}
                    display="flex"
                    alignItems="flex-start"
                    onClick={() => handleNotifClick(notif)}
                    sx={{
                      borderBottom: "1px solid #f0f2f5",
                      backgroundColor: bgColor,
                      "&:hover": {
                        backgroundColor: hoverBgColor,
                        boxShadow: "inset 1px 0 0 #dadce0, inset -1px 0 0 #dadce0, 0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)",
                        zIndex: 1,
                        cursor: "pointer"
                      },
                      transition: "all 0.1s"
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        handleSelectItem(notif.id);
                      }}
                      sx={{ mx: 1, mt: 1.0 }}
                    />
                    <IconButton
                      size="small"
                      sx={{ mr: 1, mt: 1.2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStar(notif.id, notif.starred);
                      }}
                    >
                      <Icon
                        fontSize="small"
                        sx={{ color: notif.starred === 1 ? "#f4b400" : "#dadce0" }}
                      >
                        {notif.starred === 1 ? "star" : "star_border"}
                      </Icon>
                    </IconButton>

                    <MDBox display="flex" flex={1} alignItems="flex-start" overflow="hidden">
                      <MDBox minWidth="150px" mt={1.0}>
                        <MDTypography
                          variant="button"
                          fontWeight={isUnread ? "bold" : "regular"}
                          sx={{ color: isUnread ? "#202124" : "#5f6368" }}
                        >
                          {notif.sender_name || "System"}
                        </MDTypography>
                      </MDBox>

                      <MDBox flex={1} ml={2} mt={1.0} overflow="hidden">
                        <MDTypography
                          variant="button"
                          fontWeight={isUnread ? "bold" : "regular"}
                          sx={{
                            color: isUnread ? "#202124" : "#5f6368",
                            whiteSpace: expandedId === notif.id ? "pre-wrap" : "nowrap",
                            display: "inline-block",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {expandedId === notif.id
                            ? notif.content
                            : notif.content.split('\n')[0]}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" gap={1} minWidth="140px" justifyContent="flex-end" px={2} mt={1.2}>
                      <MDTypography
                        variant="caption"
                        fontWeight={isUnread ? "bold" : "regular"}
                        color="text"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {new Date(notif.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </MDTypography>

                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleExpand(e, notif.id)}
                        sx={{
                          color: "#5f6368",
                          backgroundColor: expandedId === notif.id ? "#f1f3f4" : "transparent"
                        }}
                      >
                        <Icon>{expandedId === notif.id ? "expand_less" : "expand_more"}</Icon>
                      </IconButton>
                    </MDBox>
                  </MDBox>
                );
              })
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </DashboardLayout>
  );
}

export default Notifications;

